import { addMonths, endOfDay, format, getDaysInMonth, startOfDay } from "date-fns";

/** Source unique de vérité du coût d'une charge (PFM, Edgy, calendriers).
 *
 *  Avant ce module, quatre calculs coexistaient et se contredisaient : le PFM
 *  comptait les mois calendaires TOUCHÉS (deux prélèvements sur « 30 derniers
 *  jours », un seul sur « depuis toujours »), Edgy comptait jours / 30, et une
 *  charge annuelle pesait 100 % de son prix dans n'importe quelle période.
 *  Ici, une charge coûte ce qu'elle prélève réellement : une occurrence par
 *  date de prélèvement, entre son départ et sa fin (incluse).
 *
 *  Fuseaux : une date stockée à EXACTEMENT minuit UTC est une date calendaire
 *  (écrite par un `<input type=date>`) et se lit comme ce jour-là en heure
 *  locale ; lue telle quelle, elle tomberait la veille à l'ouest de l'UTC, et
 *  les clés d'échéance du client ne seraient plus celles du serveur (400 sur
 *  les actions, rappel annonçant un autre jour). Tout autre instant (midi UTC
 *  écrit par le serveur, horodatage réel) reste lu en heure locale. En Europe
 *  et sur un serveur UTC, les deux lectures donnent le même jour. */

export interface ScheduledCharge {
  amount: number;
  /** "monthly" | "annual" | "one_time" ; toute autre valeur ne prélève rien. */
  frequency: string;
  startDate?: Date | string | null;
  createdAt?: Date | string | null;
  /** Fin incluse. null = toujours en cours. Ignorée pour une charge ponctuelle. */
  endDate?: Date | string | null;
  /** Jour du mois (1-31), mensuel seulement. Ramené au dernier jour des mois courts. */
  billingDay?: number | null;
  /** Exceptions par échéance (AccountChargeOccurrence). Absent = aucune. */
  occurrences?: OccurrenceException[] | null;
}

export interface OccurrenceException {
  /** @db.Date : minuit UTC du jour de l'échéance. */
  dueDate: Date | string;
  /** "skipped" | "overridden" | "confirmed" | null. */
  status: string | null;
  amount?: number | null;
}

/** Clé jour d'une échéance CALCULÉE (minuit local, comme chargeOccurrences). */
export const occurrenceKey = (date: Date) => format(date, "yyyy-MM-dd");

/** Clé jour d'une `dueDate` STOCKÉE (@db.Date, relue en minuit UTC). */
export const storedDueKey = (dueDate: Date | string) =>
  (typeof dueDate === "string" ? dueDate : dueDate.toISOString()).slice(0, 10);

export interface ChargeInstance {
  date: Date;
  key: string;
  amount: number;
  status: string | null;
}

/** Plus lointaine date représentable : borne haute « aucune ». */
const NO_UPPER_BOUND = new Date(8_640_000_000_000_000);
/** Garde-fou de boucle : 100 ans de prélèvements mensuels. */
const MAX_OCCURRENCES = 1200;

/** Minuit UTC pile = date calendaire : ramenée au même jour en heure locale. */
function readStoredDate(raw: Date | string): Date {
  const date = new Date(raw);
  const midnightUtc =
    date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
  return midnightUtc ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : date;
}

export function chargeStart(charge: ScheduledCharge): Date | null {
  const raw = charge.startDate ?? charge.createdAt ?? null;
  if (!raw) return null;
  const parsed = startOfDay(readStoredDate(raw));
  return isNaN(parsed.getTime()) ? null : parsed;
}

/** Jour de prélèvement à enregistrer pour une charge saisie : un mensuel sans
 *  jour explicite se fige sur le jour calendaire de son départ (jour UTC : la
 *  date arrive d'un `<input type=date>`, à minuit UTC). Sans lui, une copie
 *  (split) démarrant à une échéance ramenée en fin de mois court perdrait le
 *  jour d'origine. */
export function defaultBillingDay(
  frequency: string,
  startDate: Date | null,
  billingDay: number | null | undefined,
): number | null {
  if (billingDay != null) return billingDay;
  if (frequency !== "monthly" || !startDate || isNaN(startDate.getTime())) return null;
  return startDate.getUTCDate();
}

function clampedDate(year: number, month: number, day: number): Date {
  const daysInMonth = getDaysInMonth(new Date(year, month, 1));
  return new Date(year, month, Math.max(1, Math.min(day, daysInMonth)));
}

/** Dates de prélèvement dans [from, to], bornes incluses. `from`/`to` sont
 *  attendues en heure locale (startOfDay/endOfDay, comme produites par
 *  `normalizePeriodBounds`) ; chaque occurrence retournée est à minuit local. */
export function chargeOccurrences(charge: ScheduledCharge, from: Date | null, to: Date): Date[] {
  const first = chargeStart(charge);
  if (!first) return [];
  const inWindow = (d: Date) => (!from || d >= from) && d <= to;

  if (charge.frequency === "one_time") return inWindow(first) ? [first] : [];

  const end = charge.endDate ? endOfDay(readStoredDate(charge.endDate)) : null;
  const last = end && end < to ? end : to;

  const billingDay =
    typeof charge.billingDay === "number" && Number.isFinite(charge.billingDay) ? charge.billingDay : null;

  const nth = (i: number): Date | null => {
    if (charge.frequency === "monthly") {
      return clampedDate(first.getFullYear(), first.getMonth() + i, billingDay ?? first.getDate());
    }
    if (charge.frequency === "annual") {
      return clampedDate(first.getFullYear() + i, first.getMonth(), first.getDate());
    }
    return null;
  };

  const out: Date[] = [];
  for (let i = 0; i < MAX_OCCURRENCES; i++) {
    const date = nth(i);
    if (!date || date > last) break;
    if (date < first) continue; // jour de prélèvement antérieur au départ, premier mois
    if (from && date < from) continue;
    out.push(date);
  }
  return out;
}

/** Échéances de [from, to] avec les exceptions appliquées : une échéance
 *  « skipped » ne coûte rien (retirée, ou à 0 avec `includeSkipped` pour
 *  l'afficher), une « overridden » coûte son propre montant. TOUT calcul de
 *  coût passe par ici : une exception ignorée quelque part redevient une
 *  fausse dépense. */
export function chargeInstances(
  charge: ScheduledCharge,
  from: Date | null,
  to: Date,
  opts: { includeSkipped?: boolean } = {},
): ChargeInstance[] {
  const byKey = new Map((charge.occurrences ?? []).map(o => [storedDueKey(o.dueDate), o]));
  const out: ChargeInstance[] = [];
  for (const date of chargeOccurrences(charge, from, to)) {
    const key = occurrenceKey(date);
    const ex = byKey.get(key);
    const status = ex?.status ?? null;
    if (status === "skipped") {
      if (opts.includeSkipped) out.push({ date, key, amount: 0, status });
      continue;
    }
    const amount = status === "overridden" && typeof ex?.amount === "number" ? ex.amount : charge.amount;
    out.push({ date, key, amount, status });
  }
  return out;
}

/** Écart des exceptions par rapport au montant nominal sur [from, to], pour les
 *  calculs qui comptent `charge.amount` × périodes sans dérouler les dates
 *  (computeReportMetrics). */
export function occurrenceAdjustment(charge: ScheduledCharge, from: Date | null, to: Date): number {
  if (!charge.occurrences?.length) return 0;
  return chargeInstances(charge, from, to, { includeSkipped: true })
    .reduce((sum, i) => sum + (i.amount - charge.amount), 0);
}

/** Montant prélevé sur la période. Sans borne haute, une charge récurrente
 *  s'arrête à `now` ; une borne haute future compte les prélèvements prévus
 *  dans la période (« ce mois » inclut le prélèvement du 20 le 5). */
export function chargeAmountBetween(
  charge: ScheduledCharge,
  from: Date | null,
  to: Date | null,
  now: Date = new Date(),
): number {
  const upper = to ?? (charge.frequency === "one_time" ? NO_UPPER_BOUND : now);
  return chargeInstances(charge, from, upper).reduce((sum, i) => sum + i.amount, 0);
}

/** Échéances affichées par les calendriers du PFM : fenêtre de 24 mois de part
 *  et d'autre d'aujourd'hui, tronquée à la fin de la charge, exceptions
 *  appliquées (une échéance sautée n'apparaît pas). */
export function chargeCalendarInstances(charge: ScheduledCharge, now: Date = new Date()): ChargeInstance[] {
  if (charge.frequency === "one_time") {
    const start = chargeStart(charge);
    return start ? chargeInstances(charge, start, endOfDay(start)) : [];
  }
  return chargeInstances(charge, addMonths(startOfDay(now), -24), addMonths(endOfDay(now), 24));
}

export function chargeCalendarDates(charge: ScheduledCharge, now: Date = new Date()): Date[] {
  return chargeCalendarInstances(charge, now).map(i => i.date);
}
